import { motion, useScroll, useTransform } from "framer-motion";
import { useT } from "@/i18n/LanguageProvider";

const HERO_IMG =
  "https://i.imgur.com/iAlEJbA.png";

export default function Hero() {
  const { t } = useT();
  const { scrollY } = useScroll();

  const textY = useTransform(scrollY, [0, 800], [0, 220]);
  const textOpacity = useTransform(scrollY, [0, 600], [1, 0.15]);
  const imgY = useTransform(scrollY, [0, 800], [0, -340]);
  const imgScale = useTransform(scrollY, [0, 700], [1, 1.1]);
  const imgRotate = useTransform(scrollY, [0, 800], [0, -4]);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative w-full overflow-hidden grain"
      style={{
        background:
          "radial-gradient(120% 80% at 70% 20%, #2d1d18 0%, #211512 55%, #160d0a 100%)",
        minHeight: "100vh",
      }}
    >
      <div className="absolute top-[78px] left-0 right-0 mx-auto max-w-[1500px] px-6 md:px-12 z-30">
        <div className="flex items-center justify-between text-[#FFF8EE]/60 label-tracked pt-6">
          <span data-testid="hero-eyebrow">{t("hero.established")}</span>
          <span className="hidden md:inline">{t("hero.single_batch")}</span>
        </div>
      </div>

      <div className="relative mx-auto max-w-[1500px] px-6 md:px-12 pt-36 md:pt-44 pb-24 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8 items-center min-h-[90vh]">
        <motion.div
          style={{ y: textY, opacity: textOpacity }}
          className="md:col-span-7 relative z-20 text-left"
        >
          <motion.p
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="label-tracked"
            style={{ color: "#C5A059" }}
            data-testid="hero-tagline"
          >
            {t("hero.tagline")}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: -60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
            data-testid="hero-headline"
            className="font-serif font-light text-[#FFF8EE] mt-8 leading-[0.92]"
            style={{ fontSize: "clamp(3rem, 8.4vw, 8rem)" }}
          >
            {t("hero.headline_1")}
            <br />
            <span className="italic" style={{ color: "#C5A059" }}>
              {t("hero.headline_2")}
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5 }}
            className="mt-10 max-w-md text-[#FFF8EE]/70 text-base md:text-lg font-light"
            data-testid="hero-description"
          >
            {t("hero.description")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7 }}
            className="mt-12 flex flex-col sm:flex-row items-start gap-4"
          >
            <a
              href="#menu"
              data-testid="hero-cta-order"
              className="btn-gold px-10 py-4 label-tracked"
            >
              {t("hero.cta_order")}
            </a>
            <a
              href="#story"
              data-testid="hero-cta-story"
              className="btn-ghost-dark px-10 py-4 label-tracked"
            >
              {t("hero.cta_story")}
            </a>
          </motion.div>
        </motion.div>

        <motion.div
          style={{ y: imgY, scale: imgScale, rotate: imgRotate }}
          className="md:col-span-5 relative z-10 flex justify-center md:justify-end items-center"
        >
          <div
            aria-hidden
            className="absolute inset-0 m-auto w-[88%] md:w-[105%] aspect-square pointer-events-none"
            style={{
              background:
                "radial-gradient(closest-side, rgba(255,248,238,0.95) 0%, rgba(255,248,238,0.85) 45%, rgba(255,248,238,0) 72%)",
              filter: "blur(2px)",
            }}
          />
          <motion.img
            src={HERO_IMG}
            alt="Floating tiramisu slice"
            data-testid="hero-tiramisu-image"
            initial={{ opacity: 0, y: 80 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.6, delay: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
            className="relative z-10 w-[78%] md:w-[110%] max-w-[640px] drop-shadow-[0_50px_70px_rgba(0,0,0,0.7)]"
            style={{ background: "transparent" }}
          />
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-3 text-[#FFF8EE]/50"
      >
        <span className="label-tracked text-[10px]">{t("hero.scroll")}</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity }}
          className="w-px h-10 bg-[#FFF8EE]/40"
        />
      </motion.div>
    </section>
  );
}
